import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Event, LobbyCheckStatus, type LobbyCheckResult } from "@uno/protocol";
import { Socket } from "socket.io-client";
import useStoreToast from "store/useStoreToast";

export default function useSocketRedirect(socket: Socket | null) {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const showToast = useStoreToast((state) => state.show);
    const wasDisconnected = React.useRef(false);

    React.useEffect(() => {
        if(!socket) {
            return;
        }

        const onDisconnect = () => {
            wasDisconnected.current = true;
        };

        const onConnect = () => {
            if(!wasDisconnected.current) {
                return;
            }

            wasDisconnected.current = false;
            const stripped = location.pathname;
            const lobbyMatch = stripped.match(/^\/lobby\/(.+)$/);
            const roomMatch = stripped.match(/^\/room\/(.+)$/);

            if(lobbyMatch) {
                socket.emit(Event.PlayerCheckLobby, lobbyMatch[1], (result: LobbyCheckResult) => {
                    if(result.status === LobbyCheckStatus.NotFound) {
                        showToast(t("lobby.closed"), "info");
                        navigate("/");
                    }
                });
            }

            if(roomMatch) {
                socket.emit(Event.PlayerCheckRoom, roomMatch[1], (active: boolean) => {
                    if(!active) {
                        navigate("/");
                    }
                });
            }
        };

        socket.on("disconnect", onDisconnect);
        socket.on("connect", onConnect);

        return () => {
            socket.off("disconnect", onDisconnect);
            socket.off("connect", onConnect);
        };
    }, [socket, location.pathname, navigate, showToast, t]);
}
